import { Dropdown, Tooltip, useDrawer } from '@iveco-ti/iveco-ds'
import {
  FileEdit,
  FileSearch,
  KeyRound,
  LoaderCircle,
  Send,
  UserRoundX,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Can } from '@/common/components/Can'
import { useInactivateUser } from '../hooks/useInactivateUser'
import { UserPaginated, userPaginatedDTO } from '../hooks/usePaginateUsers'
import { useResendActivationUser } from '../hooks/useResendActivationUser'
import { useResetUserPassword } from '../hooks/useResetUserPassword'
import { useSelectedUserStore } from '../stores/useSelectedUserStore'

type UserActiveActionsProps = {
  user: UserPaginated
}

export function UserActiveActions({ user }: UserActiveActionsProps) {
  const { t } = useTranslation(['common', 'registers'])
  const { setUser } = useSelectedUserStore()
  const { handleInactivateUser } = useInactivateUser()
  const { handleReactivateUser, isSendingReactivation } =
    useResendActivationUser()
  const { handleResetUserPassword, isPendingResetPassword } =
    useResetUserPassword()
  const drawerEditUser = useDrawer('drawer-edit-user')
  const drawerViewUser = useDrawer('drawer-view-user')

  return (
    <div className="flex flex-row gap-2 items-center justify-center">
      <Tooltip text={t('common:components.table_row.view')}>
        <button
          type="button"
          className="p-1 hover:bg-base-100 rounded"
          onClick={() => {
            setUser(user)
            drawerViewUser.open()
          }}
        >
          <FileSearch size={18} className="text-brand-700" />
        </button>
      </Tooltip>

      <Can I="edit" this={userPaginatedDTO.parse(user)}>
        <Tooltip text={t('common:components.table_row.edit')}>
          <button
            type="button"
            className="p-1 hover:bg-base-100 rounded"
            onClick={() => {
              setUser(user)
              drawerEditUser.open()
            }}
          >
            <FileEdit size={18} className="text-brand-700" />
          </button>
        </Tooltip>
      </Can>

      <Dropdown.Root closeMode="inside">
        <Dropdown.Trigger className="text-brand-700" />

        <Dropdown.Body>
          <Dropdown.List>
            {user.UserPreference.termOfUseAcceptedIP && (
              <Dropdown.Item
                onClick={() => handleResetUserPassword(user.email)}
                className="text-brand-700 text-sm font-normal"
                disabled={isPendingResetPassword}
              >
                <Dropdown.Icon icon={<KeyRound />} />
                {t('registers:users.table.table_row.dropdown.reset_password')}
              </Dropdown.Item>
            )}

            {!user.UserPreference.termOfUseAcceptedIP && (
              <Dropdown.Item
                onClick={() => handleReactivateUser(user.email)}
                className="text-brand-700 text-sm font-normal"
                disabled={isSendingReactivation}
              >
                <Dropdown.Icon
                  icon={
                    isSendingReactivation ? (
                      <LoaderCircle className="animate-spin" />
                    ) : (
                      <Send />
                    )
                  }
                />
                {t(
                  'registers:users.table.table_row.dropdown.resend_activation',
                )}
              </Dropdown.Item>
            )}

            <Can I="inactivate" this={userPaginatedDTO.parse(user)}>
              <Dropdown.Item
                onClick={() => handleInactivateUser(user.id)}
                className="text-danger-700 text-sm font-normal"
              >
                <Dropdown.Icon icon={<UserRoundX />} />
                {t('registers:users.table.table_row.dropdown.inactivate')}
              </Dropdown.Item>
            </Can>
          </Dropdown.List>
        </Dropdown.Body>
      </Dropdown.Root>
    </div>
  )
}
